import type { Schema } from "@/shared/components/Form/types";
import type {
  CreateSchemaDefinition,
  EditSchemaDefinition,
} from "@/shared/components/CRUD/types";
import {
  CreateOrganizationMutation,
  UpdateOrganizationMutation,
  OrganizationEditQuery,
  OrganizationShowQuery,
  type UpdateOrganizationInput,
} from "@/admin/entities/organizations";
import { PlatformsAutocompleteQuery } from "@/admin/entities/platforms";
import type {
  OrganizationEditQueryType,
  OrganizationShowQueryType,
} from "@/types/graphql/admin.generated";
import {
  CreateOrganizationUserMutation,
  OrganizationRoleType,
} from "@/admin/entities/organizationUsers";
import { RolesCheckboxGroupField } from "./RolesCheckboxGroupField";
import { getAssistantEditData } from "./assistantStep";

/**
 * Схемы форм для feature управления организациями
 * Используют entity мутации и запросы
 */

// Фильтры списка организаций
export const filterSchema: Schema = {
  search: {
    label: "Поиск",
    type: "text",
    placeholder: "Поиск по названию...",
    xs: 12,
    fullWidth: true,
    priority: "primary",
  },
};

// Схема создания организации
export const createSchema: CreateSchemaDefinition = {
  main: {
    label: "Создание организации",
    schema: {
      title: {
        required: true,
        label: "Name",
        helperText:
          "The rightholder title will be displayed in the partner's account, on the website, etc. ",
        type: "text",
        xs: 12,
        lg: 6,
        fullWidth: true,
      },
      slug: {
        helperText:
          "Слуг может состоять только из букв и цифр, без пробелов и других символово.",
        required: true,
        label: "Slug",
        type: "text",
        xs: 12,
        lg: 6,
        fullWidth: true,
      },
      logoId: {
        label: "Logo",
        helperText: "Minimum size: 240x240px. Format: jpeg, png.",
        required: true,
        type: "image",
        imageType: "avatar",
        xs: 12,
      },
      currency: {
        label: "Валюта отчётов",
        helperText: "Финансовые отчёты партнёра показываются в этой валюте",
        type: "select",
        xs: 12,
        lg: 6,
        fullWidth: true,
        required: true,
        options: [
          { label: "USD", value: "USD" },
          { label: "EUR", value: "EUR" },
          { label: "MDL", value: "MDL" },
          { label: "GBP", value: "GBP" },
          { label: "CAD", value: "CAD" },
          { label: "PLN", value: "PLN" },
        ],
      },
      "feeSettings.feeType": {
        label: "Тип комиссии платформы",
        required: true,
        type: "select",
        xs: 12,
        lg: 6,
        fullWidth: true,
        options: [
          { label: "Процент", value: "PERCENTAGE" },
          { label: "Фиксированная", value: "FIXED" },
          {
            label: "Фиксированная плюс процент",
            value: "FIXED_PERCENTAGE_SUM",
          },
        ],
      },
      "feeSettings.percentageFee": {
        label: "Комиссия, %",
        required: true,
        type: "number",
        xs: 12,
        lg: 3,
        fullWidth: true,
      },
      "feeSettings.fixedFeeAmount.value": {
        label: "Фиксированная комиссия",
        required: true,
        type: "number",
        xs: 12,
        lg: 3,
        fullWidth: true,
      },
      "feeSettings.fixedFeeAmount.currency": {
        label: "Валюта фиксированной комиссии",
        required: true,
        type: "select",
        xs: 12,
        lg: 3,
        fullWidth: true,
        options: [
          { label: "USD", value: "USD" },
          { label: "EUR", value: "EUR" },
          { label: "MDL", value: "MDL" },
          { label: "GBP", value: "GBP" },
          { label: "CAD", value: "CAD" },
          { label: "PLN", value: "PLN" },
        ],
      },
      "feeSettings.providerFeePayer": {
        label: "Комиссию провайдера платит",
        helperText:
          "Как комиссия Stripe или Paynet учитывается в отчётах партнёра",
        required: true,
        type: "select",
        xs: 12,
        lg: 6,
        fullWidth: true,
        options: [
          { label: "Организация", value: "ORGANIZATION" },
          { label: "Платформа", value: "OTTKIT" },
          { label: "Пополам", value: "IN_HALF" },
        ],
      },
    },
    createMutation: CreateOrganizationMutation,
  },
} as const;

// Значения по умолчанию для формы создания организации: те же дефолты, что
// заведены на бэкенде для CreateOrganizationInput.
export const createOrganizationInitialVariables = {
  currency: "USD",
  feeSettings: {
    feeType: "PERCENTAGE",
    percentageFee: 0,
    fixedFeeAmount: {
      value: 0,
      currency: "USD",
    },
    providerFeePayer: "ORGANIZATION",
  },
};

// Схема редактирования организации
export const editSchemaBase: EditSchemaDefinition = {
  details: {
    label: "Детали",
    type: "show" as const,
    getTitle: (data: unknown) => {
      const { organization } = data as OrganizationShowQueryType;
      if (!organization) return null;
      return organization.title;
    },
    showQuery: OrganizationShowQuery,
  },
  main: {
    label: "Редактировать",
    type: "form" as const,
    updateMutation: UpdateOrganizationMutation,
    editQuery: OrganizationEditQuery,
    schema: {
      title: {
        required: true,
        label: "Name",
        helperText:
          "The rightholder title will be displayed in the partner's account, on the website, etc. ",
        type: "text",
        xs: 12,
        lg: 6,
        fullWidth: true,
      },
      slug: {
        helperText:
          "Слуг может состоять только из букв и цифр, без пробелов и других символово.",
        required: true,
        label: "Slug",
        type: "text",
        xs: 12,
        lg: 6,
        fullWidth: true,
      },
      logoId: {
        label: "Logo",
        helperText: "Minimum size: 240x240px. Format: jpeg, png.",
        required: true,
        type: "image",
        imageType: "avatar",
        xs: 12,
      },
      status: {
        label: "Status",
        type: "select",
        xs: 12,
        lg: 6,
        required: true,
        fullWidth: true,
        options: [
          { label: "Active", value: "ACTIVE" },
          { label: "Inactive", value: "INACTIVE" },
        ],
      },
      platformId: {
        label: "Платформа",
        type: "autocomplete",
        helperText: "Основная платформа для организации",
        xs: 12,
        md: 6,
        fullWidth: true,
        query: PlatformsAutocompleteQuery,
        queryVariables: (params: any) => {
          return {
            filter: {
              organizationId: params?.editData?.organization?.id,
            },
          };
        },
      },
      currency: {
        label: "Валюта отчётов",
        helperText: "Финансовые отчёты партнёра показываются в этой валюте",
        type: "select",
        xs: 12,
        lg: 6,
        fullWidth: true,
        required: true,
        options: [
          { label: "USD", value: "USD" },
          { label: "EUR", value: "EUR" },
          { label: "MDL", value: "MDL" },
          { label: "GBP", value: "GBP" },
          { label: "CAD", value: "CAD" },
          { label: "PLN", value: "PLN" },
        ],
      },
      "feeSettings.feeType": {
        label: "Тип комиссии платформы",
        required: true,
        type: "select",
        xs: 12,
        lg: 6,
        fullWidth: true,
        options: [
          { label: "Процент", value: "PERCENTAGE" },
          { label: "Фиксированная", value: "FIXED" },
          {
            label: "Фиксированная плюс процент",
            value: "FIXED_PERCENTAGE_SUM",
          },
        ],
      },
      "feeSettings.percentageFee": {
        label: "Комиссия, %",
        required: true,
        type: "number",
        xs: 12,
        lg: 3,
        fullWidth: true,
      },
      "feeSettings.fixedFeeAmount.value": {
        label: "Фиксированная комиссия",
        required: true,
        type: "number",
        xs: 12,
        lg: 3,
        fullWidth: true,
      },
      "feeSettings.fixedFeeAmount.currency": {
        label: "Валюта фиксированной комиссии",
        required: true,
        type: "select",
        xs: 12,
        lg: 3,
        fullWidth: true,
        options: [
          { label: "USD", value: "USD" },
          { label: "EUR", value: "EUR" },
          { label: "MDL", value: "MDL" },
          { label: "GBP", value: "GBP" },
          { label: "CAD", value: "CAD" },
          { label: "PLN", value: "PLN" },
        ],
      },
      "feeSettings.providerFeePayer": {
        label: "Комиссию провайдера платит",
        helperText:
          "Как комиссия Stripe или Paynet учитывается в отчётах партнёра",
        required: true,
        type: "select",
        xs: 12,
        lg: 6,
        fullWidth: true,
        options: [
          { label: "Организация", value: "ORGANIZATION" },
          { label: "Платформа", value: "OTTKIT" },
          { label: "Пополам", value: "IN_HALF" },
        ],
      },
    },
    getTitle: (data: unknown) => {
      const { organization } = data as OrganizationShowQueryType;
      if (!organization) return null;
      return organization.title;
    },
    getEditData: (data: unknown): UpdateOrganizationInput => {
      // Returns UpdateOrganizationInput with __typename removed from feeSettings
      const { organization } = data as OrganizationEditQueryType;
      return {
        title: organization.title,
        slug: organization.slug,
        logoId: organization.logo?.id || null,
        status: organization.status,
        platformId: organization.platform?.id || null,
        currency: organization.currency,
        feeSettings: {
          feeType: organization.feeSettings.feeType,
          percentageFee: organization.feeSettings.percentageFee,
          fixedFeeAmount: {
            value: organization.feeSettings.fixedFeeAmount.value,
            currency: organization.feeSettings.fixedFeeAmount.currency,
          },
          providerFeePayer: organization.feeSettings.providerFeePayer,
        },
      };
    },
  },
  assistant: {
    label: "Помощник партнёра",
    type: "form" as const,
    updateMutation: UpdateOrganizationMutation,
    editQuery: OrganizationEditQuery,
    schema: {
      "assistant.enabled": {
        label: "Помощник в кабинете включён",
        type: "checkbox",
      },
      "assistant.model": {
        label: "Модель",
        type: "select",
        xs: 12,
        md: 6,
        fullWidth: true,
        options: [
          { value: "claude-sonnet-5", label: "Sonnet 5 (по умолчанию)" },
          { value: "claude-opus-5", label: "Opus 5" },
          { value: "claude-haiku-4-5", label: "Haiku 4.5" },
        ],
      },
      "assistant.monthlyBudgetUsd": {
        label: "Бюджет в месяц, $",
        type: "number",
        xs: 12,
        md: 6,
        fullWidth: true,
        nullable: true,
        helperText: "Пусто: 20",
      },
    },
    getTitle: (data: unknown) => {
      const { organization } = data as OrganizationShowQueryType;
      if (!organization) return null;
      return organization.title;
    },
    getEditData: getAssistantEditData,
  },
  billingRequisites: {
    label: "Реквизиты",
    type: "component" as const,
    componentQuery: OrganizationShowQuery,
    getTitle: (data: unknown) => {
      const { organization } = data as OrganizationShowQueryType;
      return organization.title;
    },
  },
  users: {
    label: "Сотрудники",
    type: "component" as const,
    componentQuery: OrganizationShowQuery,
    getTitle: (data: unknown) => {
      const { organization } = data as OrganizationShowQueryType;
      return organization.title;
    },
  },
  tagTypes: {
    label: "Типы тегов",
    type: "component" as const,
    componentQuery: OrganizationShowQuery,
    getTitle: (data: unknown) => {
      const { organization } = data as OrganizationShowQueryType;
      return organization.title;
    },
  },
  contractPayments: {
    label: "Платежи",
    type: "component" as const,
    componentQuery: OrganizationShowQuery,
    getTitle: (data: unknown) => {
      const { organization } = data as OrganizationShowQueryType;
      return organization.title;
    },
  },
  revenueShareReports: {
    label: "Отчёты ревшера",
    type: "component" as const,
    componentQuery: OrganizationShowQuery,
    getTitle: (data: unknown) => {
      const { organization } = data as OrganizationShowQueryType;
      return organization.title;
    },
  },
} as const;

export function buildEditSchema(
  showComponent: React.ComponentType<{ editData?: unknown; id?: string }>,
  organizationBillingRequisitesComponent: React.ComponentType<{ id: string }>,
  organizationUsersComponent: React.ComponentType<{ id: string }>,
  organizationTagTypesComponent: React.ComponentType<{ id: string }>,
  organizationContractPaymentsComponent: React.ComponentType<{ id: string }>,
  organizationRevenueShareReportsComponent: React.ComponentType<{ id: string }>
): EditSchemaDefinition {
  return {
    ...editSchemaBase,
    details: {
      ...editSchemaBase.details,
      showComponent: showComponent,
    },
    billingRequisites: {
      ...editSchemaBase.billingRequisites,
      component: organizationBillingRequisitesComponent,
    },
    users: {
      ...editSchemaBase.users,
      component: organizationUsersComponent,
    },
    tagTypes: {
      ...editSchemaBase.tagTypes,
      component: organizationTagTypesComponent,
    },
    contractPayments: {
      ...editSchemaBase.contractPayments,
      component: organizationContractPaymentsComponent,
    },
    revenueShareReports: {
      ...editSchemaBase.revenueShareReports,
      component: organizationRevenueShareReportsComponent,
    },
  };
}

export const createOrganizationUserSchema: CreateSchemaDefinition = {
  main: {
    label: "Добавление сотрудника",
    schema: {
      email: {
        label: "Email",
        type: "email",
        required: true,
        xs: 12,
        fullWidth: true,
      },
      roles: {
        label: "Роли",
        type: "fieldComponent",
        component: RolesCheckboxGroupField,
        helperText: "У сотрудника должна быть хотя бы одна роль",
        xs: 12,
      },
    },
    createMutation: CreateOrganizationUserMutation,
  },
} as const;

// Роль admin отмечена по умолчанию: минимум одна роль должна быть выбрана
export const createOrganizationUserInitialVariables = {
  roles: [OrganizationRoleType.AdminType],
};
